import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, FolderOpen, Map, Loader2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Toaster, toast } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SessionMenu() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteTyped, setDeleteTyped] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sessions`);
      setSessions(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/sessions`, { name: newName.trim() });
      navigate(`/session/${res.data.id}`);
    } catch (e) {
      toast.error('Failed to create session');
    }
    setCreating(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleteTyped !== deleteTarget.name) return;
    try {
      await axios.delete(`${API}/sessions/${deleteTarget.id}`);
      setDeleteTarget(null);
      setDeleteTyped('');
      await load();
      toast.success('Session deleted');
    } catch (e) { toast.error('Delete failed'); }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[#09090b] flex flex-col items-center pt-20 px-4" data-testid="session-menu">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in">
          <h1
            className="text-4xl sm:text-5xl font-black text-emerald-400 tracking-tight mb-3"
            style={{ fontFamily: 'Cinzel, serif' }}
            data-testid="app-title"
          >
            Mapstr
          </h1>
          <p className="text-zinc-500 text-sm">Virtual Tabletop &mdash; Fog of War</p>
        </div>

        {/* Create Session */}
        <div className="w-full max-w-md mb-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div className="glass-panel p-4">
            <div className="flex gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="New session name..."
                className="bg-zinc-900/60 border-zinc-700 text-white placeholder:text-zinc-600 flex-1"
                data-testid="new-session-input"
              />
              <Button
                onClick={handleCreate}
                disabled={!newName.trim() || creating}
                className="bg-emerald-600 hover:bg-emerald-500 text-black font-semibold shadow-[0_0_10px_rgba(16,185,129,0.3)]"
                data-testid="create-session-btn"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Session List */}
        <div className="w-full max-w-md space-y-2 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-6 h-6 text-emerald-500 animate-spin mx-auto" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12 text-zinc-600">
              <Map className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No sessions yet. Create one to begin.</p>
            </div>
          ) : (
            sessions.map((session) => (
              <div
                key={session.id}
                className="glass-panel p-4 flex items-center gap-3 cursor-pointer hover:border-emerald-500/30 transition-all group"
                onClick={() => navigate(`/session/${session.id}`)}
                data-testid={`session-${session.id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <FolderOpen className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-zinc-200 truncate">{session.name}</p>
                  <div className="flex items-center gap-1 text-[10px] text-zinc-600">
                    <Clock className="w-3 h-3" />
                    {formatDate(session.updated_at || session.created_at)}
                  </div>
                </div>
                <button
                  className="p-2 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-all"
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(session); }}
                  data-testid={`delete-session-${session.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* DB Status Footer */}
        <div className="mt-12 text-center text-[10px] text-zinc-700 font-mono" data-testid="db-info">
          MONGO_URL configured via environment variables
        </div>

        {/* Delete Confirm Dialog */}
        {deleteTarget && (
          <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setDeleteTyped(''); } }}>
            <DialogContent className="bg-zinc-950 border-zinc-800 max-w-sm" data-testid="delete-session-dialog">
              <DialogHeader>
                <DialogTitle className="text-red-400" style={{ fontFamily: 'Cinzel, serif' }}>Delete Session</DialogTitle>
                <DialogDescription className="text-zinc-500">
                  Type <span className="text-red-400 font-mono font-bold">{deleteTarget.name}</span> to confirm
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <Input
                  value={deleteTyped}
                  onChange={(e) => setDeleteTyped(e.target.value)}
                  placeholder={`Type "${deleteTarget.name}"`}
                  className="bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-600 font-mono"
                  data-testid="delete-session-confirm-input"
                  autoFocus
                />
                <Button
                  onClick={handleDelete}
                  disabled={deleteTyped !== deleteTarget.name}
                  className={`w-full font-semibold ${deleteTyped === deleteTarget.name ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-zinc-800 text-zinc-500'}`}
                  data-testid="delete-session-confirm-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete Session
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        <Toaster theme="dark" position="bottom-right" />
      </div>
    </TooltipProvider>
  );
}
